import {ISupplierCompanyPermissionsInput} from 'constants/interfaces';
import {Controller, useForm} from 'react-hook-form';
import React, {ReactNode} from 'react';
import {
  Button,
  CircularProgress,
  Grid,
  Typography,
  Switch,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  List,
} from '@material-ui/core';
import BusinessIcon from '@material-ui/icons/Business';
import StoreIcon from '@material-ui/icons/Store';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import TodayIcon from '@material-ui/icons/Today';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';
import AssignmentIcon from '@material-ui/icons/Assignment';
import ViewModuleIcon from '@material-ui/icons/ViewModule';
import PersonAddIcon from '@material-ui/icons/PersonAdd';

export function CompanyPermissionsForm(props: {
  companyPermissions?: ISupplierCompanyPermissionsInput;
  isLoading: boolean;
  submitButtonTitle: string;
  onSubmit: (permissions: ISupplierCompanyPermissionsInput) => any;
  cancelButtonTitle?: string;
  onCancel?: () => any;
  smallWidth?: boolean;
  readonly?: boolean;
}) {
  type Submission = ISupplierCompanyPermissionsInput & {
    productCategory: boolean;
    programsCategory: boolean;
    fundingCategory: boolean;
  };

  // https://levelup.gitconnected.com/using-react-hook-form-with-material-ui-components-ba42ace9507a
  const {handleSubmit, control, setValue} = useForm<Submission>({
    defaultValues: props?.companyPermissions,
  });

  const onSubmit = (submission: Submission) => {
    props.onSubmit({
      // ignoring custom fields
      customers: submission.customers,
      customerApplications: submission.customerApplications,
      customerWallet: submission.customerWallet,
      customerPrograms: submission.customerPrograms,
      customerFieldsAndLayers: submission.customerFieldsAndLayers,
      products: submission.products,
      productsGlobalViewOnly: submission.productsGlobalViewOnly,
      quotes: submission.quotes,
      orders: submission.orders,
      programs: submission.programs,
      programsViewOnly: submission.programsViewOnly,
      fundingApplications: submission.fundingApplications,
      fundingApplicationsViewOnly: submission.fundingApplicationsViewOnly,
      purchaseOrders: submission.purchaseOrders,
      orderBundles: submission.orderBundles,
      onboarding: submission.onboarding,
    });
  };

  return (
    <div className="generic-form-container" style={{width: '100%'}}>
      <div className="form-container" style={{width: '100%'}}>
        <form
          className="form"
          onSubmit={handleSubmit(onSubmit)}
          style={{width: '100%'}}
        >
          <Grid container className="form-section" spacing={4}>
            <Grid item xs={12}>
              <Typography variant={'subtitle2'} gutterBottom>
                Company Permissions
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <List dense>
                <Controller
                  name={'customers'}
                  control={control}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <PermissionListItem
                      icon={<BusinessIcon />}
                      title="Customers"
                      subTitle="Manage customer profiles"
                      value={value || false}
                      onChange={(newValue) => {
                        onChange(newValue);
                        setValue('customerApplications', newValue);
                        setValue('customerWallet', newValue);
                        setValue('customerPrograms', newValue);
                        setValue('customerFieldsAndLayers', newValue);
                      }}
                      disabled={props.readonly || props.isLoading}
                    />
                  )}
                />
                <Controller
                  name={'customerApplications'}
                  control={control}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <PermissionListItem
                      // icon={<BusinessIcon />}
                      title="Applications"
                      subTitle="View customer applications"
                      value={value || false}
                      onChange={(newValue) => {
                        onChange(newValue);
                        if (newValue) setValue('customers', true);
                      }}
                      disabled={props.readonly || props.isLoading}
                      nested
                    />
                  )}
                />
                <Controller
                  name={'customerWallet'}
                  control={control}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <PermissionListItem
                      // icon={<BusinessIcon />}
                      title="Wallet"
                      subTitle="Access customer wallets"
                      value={value || false}
                      onChange={(newValue) => {
                        onChange(newValue);
                        if (newValue) setValue('customers', true);
                      }}
                      disabled={props.readonly || props.isLoading}
                      nested
                    />
                  )}
                />
                <Controller
                  name={'customerFieldsAndLayers'}
                  control={control}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <PermissionListItem
                      // icon={<BusinessIcon />}
                      title="Fields and Layers"
                      subTitle="View mapped farm data"
                      value={value || false}
                      onChange={(newValue) => {
                        onChange(newValue);
                        if (newValue) setValue('customers', true);
                      }}
                      disabled={props.readonly || props.isLoading}
                      nested
                    />
                  )}
                />
                <Controller
                  name={'customerPrograms'}
                  control={control}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <PermissionListItem
                      // icon={<BusinessIcon />}
                      title="Programs"
                      subTitle="View customer programs"
                      value={value || false}
                      onChange={(newValue) => {
                        onChange(newValue);
                        if (newValue) setValue('customers', true);
                      }}
                      disabled={props.readonly || props.isLoading}
                      nested
                    />
                  )}
                />
                <Controller
                  name={'productCategory'}
                  control={control}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <PermissionListItem
                      icon={<StoreIcon />}
                      title="Products"
                      subTitle="Product catalogue access"
                      value={value || false}
                      onChange={(newValue) => {
                        onChange(newValue);
                        if (newValue) {
                          setValue('products', true);
                        } else {
                          setValue('products', false);
                          setValue('productsGlobalViewOnly', false);
                        }
                      }}
                      disabled={props.readonly || props.isLoading}
                    />
                  )}
                />
                <Controller
                  name={'products'}
                  control={control}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <PermissionListItem
                      // icon={<StoreIcon />}
                      title="Manage Products"
                      subTitle="Add and edit products"
                      value={value || false}
                      onChange={(newValue) => {
                        onChange(newValue);
                        if (newValue) setValue('productCategory', true);
                        if (newValue) setValue('productsGlobalViewOnly', false);
                      }}
                      disabled={props.readonly || props.isLoading}
                      nested
                    />
                  )}
                />
                <Controller
                  name={'productsGlobalViewOnly'}
                  control={control}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <PermissionListItem
                      // icon={<StoreIcon />}
                      title="Global Product View"
                      subTitle="View shared catalogue"
                      value={value || false}
                      onChange={(newValue) => {
                        onChange(newValue);
                        if (newValue) setValue('productCategory', true);
                        if (newValue) setValue('products', false);
                      }}
                      disabled={props.readonly || props.isLoading}
                      nested
                    />
                  )}
                />
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <List dense>
                <Controller
                  name={'quotes'}
                  control={control}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <PermissionListItem
                      icon={<ShoppingCartIcon />}
                      title="Quotes"
                      subTitle="Create and manage quotes"
                      value={value || false}
                      onChange={onChange}
                      disabled={props.readonly || props.isLoading}
                    />
                  )}
                />
                <Controller
                  name={'orders'}
                  control={control}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <PermissionListItem
                      icon={<LocalShippingIcon />}
                      title="Orders"
                      subTitle="Track and manage orders"
                      value={value || false}
                      onChange={onChange}
                      disabled={props.readonly || props.isLoading}
                    />
                  )}
                />
                <Controller
                  name={'programsCategory'}
                  control={control}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <PermissionListItem
                      icon={<TodayIcon />}
                      title="Programs"
                      subTitle="Input program access"
                      value={value || false}
                      onChange={(newValue) => {
                        onChange(newValue);
                        if (newValue) {
                          setValue('programs', true);
                        } else {
                          setValue('programs', false);
                          setValue('programsViewOnly', false);
                        }
                      }}
                      disabled={props.readonly || props.isLoading}
                    />
                  )}
                />
                <Controller
                  name={'programs'}
                  control={control}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <PermissionListItem
                      // icon={<TodayIcon />}
                      title="Manage Programs"
                      subTitle="Create and edit programs"
                      value={value || false}
                      onChange={(newValue) => {
                        onChange(newValue);
                        if (newValue) setValue('programsCategory', true);
                        if (newValue) setValue('programsViewOnly', false);
                      }}
                      disabled={props.readonly || props.isLoading}
                      nested
                    />
                  )}
                />
                <Controller
                  name={'programsViewOnly'}
                  control={control}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <PermissionListItem
                      // icon={<TodayIcon />}
                      title="View Programs Only"
                      subTitle="Read-only program access"
                      value={value || false}
                      onChange={(newValue) => {
                        onChange(newValue);
                        if (newValue) setValue('programsCategory', true);
                        if (newValue) setValue('programs', false);
                      }}
                      disabled={props.readonly || props.isLoading}
                      nested
                    />
                  )}
                />
                <Controller
                  name={'fundingCategory'}
                  control={control}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <PermissionListItem
                      icon={<AccountBalanceIcon />}
                      title="Funding Applications"
                      subTitle="Funding workflow access"
                      value={value || false}
                      onChange={(newValue) => {
                        onChange(newValue);
                        if (newValue) {
                          setValue('fundingApplications', true);
                        } else {
                          setValue('fundingApplications', false);
                          setValue('fundingApplicationsViewOnly', false);
                        }
                      }}
                      disabled={props.readonly || props.isLoading}
                    />
                  )}
                />
                <Controller
                  name={'fundingApplications'}
                  control={control}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <PermissionListItem
                      // icon={<AccountBalanceIcon />}
                      title="Manage Applications"
                      subTitle="Review and update applications"
                      value={value || false}
                      onChange={(newValue) => {
                        onChange(newValue);
                        if (newValue) setValue('fundingCategory', true);
                        if (newValue)
                          setValue('fundingApplicationsViewOnly', false);
                      }}
                      disabled={props.readonly || props.isLoading}
                      nested
                    />
                  )}
                />
                <Controller
                  name={'fundingApplicationsViewOnly'}
                  control={control}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <PermissionListItem
                      // icon={<AccountBalanceIcon />}
                      title="View Applications Only"
                      subTitle="Read-only application access"
                      value={value || false}
                      onChange={(newValue) => {
                        onChange(newValue);
                        if (newValue) setValue('fundingCategory', true);
                        if (newValue) setValue('fundingApplications', false);
                      }}
                      disabled={props.readonly || props.isLoading}
                      nested
                    />
                  )}
                />
              </List>
            </Grid>
            <Grid item xs={12}>
              <Typography
                variant={'subtitle2'}
                gutterBottom
                style={{marginTop: 8}}
              >
                Additional Features
              </Typography>
              <List dense>
                <Controller
                  name={'purchaseOrders'}
                  control={control}
                  render={({field: {onChange, value}}) => (
                    <PermissionListItem
                      icon={<AssignmentIcon />}
                      title="Purchase Orders"
                      subTitle="View and manage purchase orders"
                      value={value || false}
                      onChange={onChange}
                      disabled={props.readonly || props.isLoading}
                    />
                  )}
                />
                <Controller
                  name={'orderBundles'}
                  control={control}
                  render={({field: {onChange, value}}) => (
                    <PermissionListItem
                      icon={<ViewModuleIcon />}
                      title="Order Bundles"
                      subTitle="Group and manage order bundles"
                      value={value || false}
                      onChange={onChange}
                      disabled={props.readonly || props.isLoading}
                    />
                  )}
                />
                <Controller
                  name={'onboarding'}
                  control={control}
                  render={({field: {onChange, value}}) => (
                    <PermissionListItem
                      icon={<PersonAddIcon />}
                      title="Onboarding"
                      subTitle="Onboard new companies and users"
                      value={value || false}
                      onChange={onChange}
                      disabled={props.readonly || props.isLoading}
                    />
                  )}
                />
              </List>
            </Grid>
          </Grid>
          {!props.readonly && (
            <Grid container spacing={2} style={{marginTop: '1rem'}}>
              <Grid item xs={props.smallWidth ? 12 : 6}>
                {props.cancelButtonTitle && (
                  <Button
                    className={'secondary-btn'}
                    variant="contained"
                    fullWidth
                    disableElevation
                    disabled={props.isLoading}
                    onClick={props.onCancel}
                  >
                    {props.cancelButtonTitle}
                  </Button>
                )}
              </Grid>
              <Grid item xs={props.smallWidth ? 12 : 6}>
                <Button
                  className={'primary-btn'}
                  type="submit"
                  variant="contained"
                  fullWidth
                  disableElevation
                  color="primary"
                  disabled={props.isLoading}
                >
                  {props.isLoading ? (
                    <CircularProgress
                      color={'primary'}
                      size={24}
                      thickness={6}
                    />
                  ) : (
                    props.submitButtonTitle
                  )}
                </Button>
              </Grid>
            </Grid>
          )}
        </form>
      </div>
    </div>
  );
}

function PermissionListItem(props: {
  icon?: ReactNode;
  title: string;
  subTitle: string;
  value: boolean;
  onChange: (value: boolean) => any;
  disabled: boolean;
  nested?: boolean;
}) {
  return (
    <ListItem style={props.nested ? {paddingLeft: 72 + 32} : {}}>
      {props.icon && <ListItemIcon>{props.icon}</ListItemIcon>}
      <ListItemText primary={props.title} secondary={props.subTitle} />
      <ListItemSecondaryAction>
        <Switch
          edge="end"
          color="primary"
          checked={props.value || false}
          onChange={(event) => props.onChange(event.target.checked)}
          disabled={props.disabled}
        />
      </ListItemSecondaryAction>
    </ListItem>
  );
}
